<template>
  <div class="flex flex-col items-center min-h-full">
    <svg
      style="width: 0; height: 0; position: absolute;"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id="fillPurple" x1="1" x2="1" y1="0" y2="1">
          <stop offset="0%" stop-color="#48BB78"></stop>
          <stop offset="15%" stop-color="#48BB78"></stop>
          <stop offset="100%" stop-color="#48BB7844"></stop>
        </linearGradient>
      </defs>
    </svg>

    <AppHeader />
    <main
      class="flex flex-col items-center justify-center w-full h-full min-h-full pb-6 text-center bg-dark-100 dark:bg-dark-800"
    >
      <h1 class="px-4 py-5 mb-8 text-4xl text-center font-heading">
        Orienteering Unlocked - Trail O - Stats
      </h1>
      <template v-if="data.length > 0">
        <div
          v-for="[index, answer] of game.solution.entries()"
          :key="index"
          class="px-6 py-3 mb-8 bg-white rounded-lg shadow-lg md:w-10/12 md:mx-1/12 xl:w-8/12 xl:mx-2/12 dark:bg-dark-600 hover:shadow-xl"
        >
          <h2 class="pb-2 text-3xl font-heading">Leg {{ index + 1 }}</h2>
          <h3 class="pb-1 text-xl font-heading">Answer: {{ answer }}</h3>
          <div class="flex flex-row flex-wrap items-center justify-around pb-4">
            <div>
              <p>
                <span class="ml-3 font-heading">Attempts:</span>
                <span class="align-middle"> {{ stats[index].total }}</span>
              </p>
              <p>
                <span
                  class="inline-block w-4 h-4 align-middle bg-green-500"
                ></span>
                <span class="ml-3 align-middle font-heading">Correct:</span>
                <span class="align-middle"> {{ stats[index].correct }}</span>
              </p>
              <p>
                <span
                  class="inline-block w-4 h-4 align-middle bg-red-500"
                ></span>
                <span class="ml-3 align-middle font-heading">Incorrect:</span>
                <span class="align-middle">
                  {{ stats[index].total - stats[index].correct }}</span
                >
              </p>
            </div>

            <div>
              <p>Times Don't Include Penalty</p>
              <p>
                <span class="ml-3 font-heading">Average Time:</span>
                {{ stats[index].averageTime }}
              </p>
              <p>
                <span class="ml-3 font-heading">Average Correct Time:</span>
                {{ stats[index].averageCorrectTime }}
              </p>
              <p>
                <span class="ml-3 font-heading">Average Incorrect Time:</span>
                {{ stats[index].averageIncorrectTime }}
              </p>
            </div>
            <div>
              <p>
                <span
                  class="inline-block w-4 h-4 align-middle bg-teal-500"
                ></span>
                <span class="ml-3 font-heading">A:</span>
                {{ stats[index].A }}
              </p>
              <p>
                <span
                  class="inline-block w-4 h-4 align-middle bg-green-500"
                ></span>
                <span class="ml-3 font-heading">B:</span>
                {{ stats[index].B }}
              </p>
              <p>
                <span
                  class="inline-block w-4 h-4 align-middle bg-blue-500"
                ></span>
                <span class="ml-3 font-heading">C:</span>
                {{ stats[index].C }}
              </p>
              <p>
                <span
                  class="inline-block w-4 h-4 align-middle bg-green-800"
                ></span>
                <span class="ml-3 font-heading">D:</span>
                {{ stats[index].D }}
              </p>
              <p>
                <span
                  class="inline-block w-4 h-4 align-middle bg-teal-700"
                ></span>
                <span class="ml-3 font-heading">E:</span>
                {{ stats[index].E }}
              </p>
              <p>
                <span
                  class="inline-block w-4 h-4 align-middle bg-green-300"
                ></span>
                <span class="ml-3 font-heading">F:</span>
                {{ stats[index].F }}
              </p>
              <p>
                <span class="inline-block w-4 h-4 align-middle bg-black"></span>
                <span class="ml-3 font-heading">Z:</span>
                {{ stats[index].Z }}
              </p>
            </div>

            <PieChart
              :initial-values="[
                stats[index].A,
                stats[index].B,
                stats[index].C,
                stats[index].D,
                stats[index].E,
                stats[index].F,
                stats[index].Z,
              ]"
            />
          </div>
          <TrendChart
            v-if="data.length > 0 && times[index]"
            :id="`graph-${index}`"
            class="font-body"
            :class="{
              long: xAxisLabels[index].length > 62,
              tall: yLabels(index) > 14,
            }"
            :interactive="true"
            :datasets="[
              {
                data: times[index].incorrect,
                smooth: true,
                fill: true,
                showPoints: true,
                className: 'incorrect-curve',
              },
              {
                data: times[index].correct,
                smooth: true,
                fill: true,
                showPoints: true,
                className: 'correct-curve',
              },
            ]"
            :grid="{
              verticalLines: true,
              horizontalLines: true,
            }"
            :min="0"
            :labels="{
              xLabels: xAxisLabels[index],
              yLabels: yLabels(index),
            }"
          />
          <p class="font-heading">Times (s)</p>
        </div>
      </template>
    </main>
    <AppFooter />
  </div>
</template>
<script>
import AppHeader from '@/components/layout/AppHeader'
import AppFooter from '@/components/layout/AppFooter'
import PieChart from '@/components/PieChart'
import TrendChart from 'vue-trend-chart'

import Games from '@/assets/LockdownGames'
import Data from '@/assets/BestResults'

export default {
  components: {
    AppHeader,
    AppFooter,
    PieChart,
    TrendChart,
  },

  data: () => ({
    games: Games,
    game: Games.filter((game) => game.name === 'Trail O')[0],

    data: Data.map((data) => data.fields),
  }),

  computed: {
    xAxisLabels: function () {
      return this.times.map((time, i) => [
        ...Array(
          Math.max(this.times[i].correct.length, this.times[i].incorrect.length)
        ).keys(),
      ])
    },

    times: function () {
      if (this.data.length >= 0)
        return this.game.solution.map((leg, i) => this.getTimes(i))
      else return []
    },

    stats: function () {
      if (this.data.length >= 0)
        return this.game.solution.map((leg, i) => this.getStats(i))
      else return []
    },
  },

  /*
  mounted: async function () {
    this.data = await fetch('/api/getResultsData')
      .then((response) => response.json())
      .then((response) =>
        response.data.filter((result) => result.raceNumber > 10)
      )
  },
*/
  methods: {
    yLabels: function (num) {
      const max = Math.max(
        ...this.times[num].correct,
        ...this.times[num].incorrect
      )
      return max + 1
    },

    counts: function (array, size) {
      const counts = Array(size || 1).fill(0)

      for (const num of array) {
        if (counts[num]) counts[num] += 1
        else counts[num] = 1
      }
      return counts
    },

    getTimes: function (id) {
      const simpleData = this.data
        .filter((result) => result.trailoChoices && result.trailoTimes)
        .map((result) => ({
          correct:
            JSON.parse(result.trailoChoices)[id] === this.game.solution[id],
          time: JSON.parse(result.trailoTimes)[id],
        }))
        .filter((result) => result.time && result.time < 60000)

      const maxTime =
        Math.max(
          ...simpleData
            .filter((result) => result.time)
            .map((result) => Math.round(result.time / 1000))
        ) + 1

      return {
        correct: this.counts(
          simpleData
            .filter((result) => result.correct)
            .map((result) => Math.round(result.time / 1000)),
          maxTime
        ),
        incorrect: this.counts(
          simpleData
            .filter((result) => !result.correct && result.time)
            .map((result) => Math.round(result.time / 1000)),
          maxTime
        ),
      }
    },

    getStats: function (id) {
      const simpleData = this.data
        .filter((result) => result.trailoChoices && result.trailoTimes)
        .map((result) => ({
          correct:
            JSON.parse(result.trailoChoices)[id] === this.game.solution[id],
          time: JSON.parse(result.trailoTimes)[id],
          choice: JSON.parse(result.trailoChoices)[id],
        }))
        .filter((result) => result.time)
        .map((result) => {
          result.time = result.time / 1000
          return result
        })

      return {
        total: simpleData.length,
        correct: simpleData.filter((result) => result.correct).length,
        A: simpleData.filter((result) => result.choice === 'A').length,
        B: simpleData.filter((result) => result.choice === 'B').length,
        C: simpleData.filter((result) => result.choice === 'C').length,
        D: simpleData.filter((result) => result.choice === 'D').length,
        E: simpleData.filter((result) => result.choice === 'E').length,
        F: simpleData.filter((result) => result.choice === 'F').length,
        Z: simpleData.filter((result) => result.choice === 'Z').length,

        averageTime: (
          simpleData.map((result) => result.time).reduce((a, b) => a + b, 0) /
          simpleData.length
        ).toFixed(2),
        averageCorrectTime: (
          simpleData
            .filter((result) => result.correct)
            .map((result) => result.time)
            .reduce((a, b) => a + b, 0) /
          simpleData.filter((result) => result.correct).length
        ).toFixed(2),
        averageIncorrectTime: (
          simpleData
            .filter((result) => !result.correct)
            .map((result) => result.time)
            .reduce((a, b) => a + b, 0) /
          simpleData.filter((result) => !result.correct).length
        ).toFixed(2),
      }
    },
  },
}
</script>
<style>
div {
  transition: 0.3s;
}
.vtc {
  transition: 0.3s;
  user-select: none;
}
.grid line,
.labels {
  stroke: #48bb7844;
}

.labels .x-labels g:nth-child(even) {
  display: none;
}

.long .labels .x-labels g {
  display: none;
}

.long .labels .x-labels g:nth-child(0),
.long .labels .x-labels g:nth-child(4n + 1) {
  display: block;
}

.tall .labels .y-labels g {
  display: none;
}

.tall .labels .y-labels g:nth-child(0),
.tall .labels .y-labels g:nth-child(odd) {
  display: block;
}

.correct-curve .stroke {
  stroke: #48bb78;
  stroke-width: 2;
  z-index: 3;
}

.correct-curve .point {
  stroke: #48bb78;
  fill: #48bb78;
  display: none;
}
.correct-curve .fill {
  fill: url(#fillPurple);
  fill-opacity: 0.5;
}

.incorrect-curve .stroke {
  stroke: #f00;
  stroke-width: 2;
  z-index: 2;
}

.incorrect-curve .point {
  stroke: #f00;
  fill: #f00;
  display: none;
}

.incorrect-curve .fill {
  fill-opacity: 0;
}

.point.is-active {
  stroke-width: 4;
  display: block;
}

.active-line {
  stroke: #48bb78dd;
}

.vtc {
  height: 350px;
  font-size: 12px;
}
</style>
